import axios, { AxiosError, AxiosInstance } from 'axios';
import store from 'store/config';
import { destroy } from 'store/slices/user';
import { toastError } from 'components/Toast';
import { AUTH_PATHS_REGEX } from 'helpers/authPaths';
import envScalesClient from 'actions/envScales/axiosConfig';
import videoObsClient from 'actions/video-obs/axiosConfig';
import coreClient from 'actions/core/axiosCore';

export const SESSION_EXPIRED_HEADER = 'x-teachstone-session-expired';

/**
 * Turns a 401 into the session-expiry flow: show the server's message, clear
 * the user, and let App.js redirect to login.
 *
 * Gates on the X-Teachstone-Session-Expired response header, not the bare
 * status. Only lib/custom_failure.rb ever sets it, and only when Warden's own
 * auth check fails -- an ordinary 401 from unrelated controller code never
 * carries it. See doc/security/session_expiration.md.
 *
 * Only toasts/clears the store if this tab had already seen an authenticated
 * user (`store.getState().user.id`). A cold load (new tab, refresh, deep
 * link) always starts with an empty store, so its first 401 is
 * indistinguishable from "never logged in" -- toasting there would describe
 * an event this tab never witnessed.
 */
export function handleSessionExpiry(error: AxiosError<{ error?: string }>) {
  const hasSessionExpiredHeader =
    error?.response?.status === 401 &&
    error?.response?.headers?.[SESSION_EXPIRED_HEADER] === 'true';
  const isOnAuthPage = AUTH_PATHS_REGEX.test(window.location.pathname);
  const wasAlreadyAuthenticated = Boolean(store.getState().user.id);

  if (!hasSessionExpiredHeader || isOnAuthPage || !wasAlreadyAuthenticated) {
    return Promise.reject(error);
  }

  // Server message is already localized (devise.failure.timeout); fall back
  // to toastError's generic copy if a 401 ever lacks that shape.
  const serverMessage = error?.response?.data?.error;
  toastError(serverMessage ? { description: serverMessage } : {});
  store.dispatch(destroy());

  // useAsync.ts checks this flag to skip its own error toast on the same
  // request, so the user doesn't see a second, conflicting message.
  (
    error as AxiosError & { isSessionExpiredRedirect?: boolean }
  ).isSessionExpiredRedirect = true;
  return Promise.reject(error);
}

/**
 * Every Axios instance that sends the shared session cookie
 * (`withCredentials: true` against a Teachstone origin) -- the header, not
 * which client a 401 arrived on, decides whether it's treated as expiry.
 *
 * core is included despite never sending the header (a legacy app that never
 * implemented this feature): it's still Teachstone-owned, so trusting it is
 * safe, and its unmarked 401s just fail the header check like any other.
 *
 * KalturaAPI is deliberately NOT in this list: it has no credentials against
 * a real third party (kaltura.com), and the header is just a string match
 * with nothing cryptographically tying it to onelogin -- a Kaltura response
 * carrying this exact header (deliberately or not) could force a false
 * logout. Never add it back.
 */
const SESSION_BEARING_CLIENTS: AxiosInstance[] = [
  envScalesClient,
  videoObsClient,
  coreClient
];

export function init() {
  axios.interceptors.response.use(response => response, handleSessionExpiry);

  SESSION_BEARING_CLIENTS.forEach(client => {
    client.interceptors.response.use(response => response, handleSessionExpiry);
  });
}
